import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Loader from "../loader/Loader";
import OrgChart from "./OrgChart";
import { useOrgChartData } from "./useOrgChartData";

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 240px;
`;

const Deactivated = styled.div`
  position: relative;
  margin-left: auto;
  align-self: flex-end;

  .dz-trigger { display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 14px;
    border: 1px solid #e6edf3; border-radius: 9px; background: #fff; cursor: pointer;
    font: inherit; font-size: 13px; font-weight: 600; color: #173453;
    box-shadow: 0 1px 3px rgba(23, 52, 83, 0.06); transition: border-color 0.15s, box-shadow 0.15s; }
  .dz-trigger:hover, .dz-trigger.is-open { border-color: #e3b7b7; box-shadow: 0 2px 8px rgba(178, 59, 59, 0.12); }
  .dz-trigger .dz-dot { width: 7px; height: 7px; border-radius: 50%; background: #d9534f; }
  .dz-trigger .dz-count { font-size: 11px; font-weight: 700; color: #b23b3b; background: #fdecec;
    padding: 1px 8px; border-radius: 999px; }

  .dz-pop { position: absolute; right: 0; top: calc(100% + 8px); z-index: 40; min-width: 480px;
    background: #fff; border: 1px solid #e6edf3; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(23, 52, 83, 0.18);
    opacity: 0; visibility: hidden; transform: translateY(-4px); pointer-events: none;
    transition: opacity 0.15s, transform 0.15s, visibility 0.15s; }
  .dz-pop.open { opacity: 1; visibility: visible; transform: translateY(0); pointer-events: auto; }

  .dz-head { display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 12px 14px 12px 18px; border-bottom: 1px solid #eef2f6; }
  .dz-title { font-size: 13px; font-weight: 700; color: #173453; }
  .dz-close { border: 0; background: transparent; cursor: pointer; line-height: 1;
    font-size: 20px; color: #8a9bad; padding: 0 4px; border-radius: 6px; }
  .dz-close:hover { color: #173453; background: #eef2f6; }

  table { width: 100%; border-collapse: collapse; font-size: 13px; text-align: left; }
  th { font-weight: 600; color: #7089a0; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
    padding: 9px 18px; background: #f7fafc; border-bottom: 1px solid #eef2f6; }
  td { padding: 11px 18px; border-bottom: 1px solid #f1f5f8; color: #173453; }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover { background: #f9fbfd; }
  .dz-name { font-weight: 600; }
  .dz-email { font-weight: 400; font-size: 11px; color: #8a9bad; margin-top: 1px; }
  .dz-muted { color: #8a9bad; }
`;

/**
 * Format an ISO date string as "02 Jan 2024", or "—" when missing/invalid.
 * @param {string} s
 * @returns {string}
 */
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Human label for a location row: its unit code, else its address parts, else id.
 * @param {object} loc
 * @returns {string}
 */
const locationLabel = (loc) =>
  loc?.unitCode ||
  [loc?.location?.area, loc?.location?.city, loc?.location?.state, loc?.location?.country]
    .filter(Boolean)
    .join(", ") ||
  `Location ${loc?.id}`;

/** Settings tab: org chart derived from capability assignments for a FY + location. */
const OrgChartFromAuth = () => {
  const {
    financialYears,
    locations,
    selectedFYId,
    setSelectedFYId,
    selectedLocationId,
    setSelectedLocationId,
    treeData,
    deactivated,
    loading,
  } = useOrgChartData();

  const [dzOpen, setDzOpen] = useState(false);
  const dzRef = useRef(null);

  useEffect(() => {
    if (!dzOpen) return undefined;
    const onDown = (e) => { if (dzRef.current && !dzRef.current.contains(e.target)) setDzOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setDzOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [dzOpen]);

  return (
    <div>
      <Toolbar>
        <Field>
          <label className="form-label" htmlFor="ocx-fy">Financial Year</label>
          <select
            id="ocx-fy"
            className="form-select"
            value={selectedFYId}
            onChange={(e) => setSelectedFYId(e.target.value)}
          >
            <option value="">Select Financial Year</option>
            {financialYears.map((fy) => (
              <option key={fy.id} value={fy.id}>
                {fy.financial_year_value}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          <label className="form-label" htmlFor="ocx-loc">Location</label>
          <select
            id="ocx-loc"
            className="form-select"
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            <option value="">Select Location</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {locationLabel(loc)}
              </option>
            ))}
          </select>
        </Field>

        {!loading && deactivated.length > 0 && (
          <Deactivated ref={dzRef}>
            <button
              type="button"
              className={`dz-trigger ${dzOpen ? "is-open" : ""}`}
              aria-expanded={dzOpen}
              onClick={() => setDzOpen((o) => !o)}
            >
              <span className="dz-dot" />
              Deactivated users
              <span className="dz-count">{deactivated.length}</span>
            </button>
            <div className={`dz-pop ${dzOpen ? "open" : ""}`}>
              <div className="dz-head">
                <span className="dz-title">Deactivated users</span>
                <button type="button" className="dz-close" aria-label="Close" onClick={() => setDzOpen(false)}>×</button>
              </div>
              <table>
                <thead>
                  <tr><th>Name</th><th>Role</th><th>Deactivated on</th><th>By</th></tr>
                </thead>
                <tbody>
                  {deactivated.map((u) => (
                    <tr key={u.userId}>
                      <td className="dz-name">
                        {u.name}
                        {u.email && <div className="dz-email">{u.email}</div>}
                      </td>
                      <td>{u.role}</td>
                      <td>{fmtDate(u.deactivatedOn)}</td>
                      <td className={u.deactivatedBy ? "" : "dz-muted"}>{u.deactivatedBy || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Deactivated>
        )}
      </Toolbar>

      {loading && <Loader />}

      {!loading && !selectedFYId && (
        <p className="text-muted">Select a financial year and location to view the org chart.</p>
      )}

      {!loading && treeData && <OrgChart data={treeData} />}

      {!loading && selectedFYId && selectedLocationId && treeData &&
        (treeData.children || []).length === 0 && (
          <p className="text-muted">No org-chart data for this financial year and location.</p>
        )}
    </div>
  );
};

export default OrgChartFromAuth;
